<template>
  <div class="suporte-page">
    <div class="modern-header">
      <div class="header-container">
        <div class="breadcrumb-section">
          <nav class="breadcrumb">
            <button type="button" class="breadcrumb-item" @click="$router.push('/dashboard')">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
              {{ t('common.breadcrumb.dashboard') }}
            </button>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">{{ t('suporte.breadcrumb') }}</span>
          </nav>
        </div>

        <div class="title-section">
          <div class="title-content">
            <div class="title-icon title-icon--support">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/>
              </svg>
            </div>
            <div class="title-text">
              <h1 class="main-title">{{ t('suporte.title') }}</h1>
              <p class="subtitle">{{ t('suporte.subtitle') }}</p>
            </div>
          </div>

          <div class="workspace-badge badge-discord">
            <div class="badge-icon badge-icon--discord">
              <img
                :src="discordIconSrc"
                alt=""
                class="discord-icon discord-icon--sm discord-icon--white"
                aria-hidden="true"
              />
            </div>
            <span class="badge-text">{{ t('suporte.badgeDiscord') }}</span>
          </div>
        </div>

        <div class="actions-section">
          <button type="button" class="action-button secondary" @click="copyInviteLink">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
            </svg>
            {{ copied ? t('suporte.copyInviteDone') : t('suporte.copyInvite') }}
          </button>
          <a
            :href="discordInviteUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="action-button primary discord-cta"
          >
            <img
              :src="discordIconSrc"
              alt=""
              class="discord-icon discord-icon--sm discord-icon--white"
              aria-hidden="true"
            />
            {{ t('suporte.joinDiscord') }}
          </a>
        </div>
      </div>
    </div>

    <div class="suporte-content">
      <section class="discord-hero">
        <div class="discord-hero__glow" aria-hidden="true" />
        <div class="discord-hero__inner">
          <div class="discord-hero__brand">
            <div class="discord-hero__logo" aria-hidden="true">
              <img
                :src="discordIconSrc"
                alt=""
                class="discord-icon discord-icon--lg discord-icon--white"
              />
            </div>
            <div>
              <p class="discord-hero__eyebrow">{{ t('suporte.heroEyebrow') }}</p>
              <h2 class="discord-hero__title">{{ t('suporte.heroTitle') }}</h2>
            </div>
          </div>

          <p class="discord-hero__lead">
            {{ t('suporte.heroLeadPart1') }}<strong>{{ t('suporte.heroLeadTicket') }}</strong>{{ t('suporte.heroLeadPart2') }}
          </p>

          <div class="discord-hero__link-box">
            <span class="discord-hero__link-label">{{ t('suporte.inviteLabel') }}</span>
            <a
              :href="discordInviteUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="discord-hero__link"
            >{{ discordInviteUrl }}</a>
          </div>

          <div class="discord-hero__actions">
            <a
              :href="discordInviteUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="hero-btn hero-btn--primary"
            >
              {{ t('suporte.openDiscordCta') }}
            </a>
            <button type="button" class="hero-btn hero-btn--ghost" @click="copyInviteLink">
              {{ copied ? t('suporte.copyLinkDone') : t('suporte.copyLink') }}
            </button>
          </div>
        </div>
      </section>

      <section class="support-panel steps-section">
        <header class="support-panel__header">
          <div class="support-panel__header-icon" aria-hidden="true">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
            </svg>
          </div>
          <div>
            <h3 class="section-heading">{{ t('suporte.stepsHeading') }}</h3>
            <p class="section-subheading">{{ t('suporte.stepsSubheading') }}</p>
          </div>
        </header>

        <div class="steps-list">
          <article
            v-for="(step, index) in steps"
            :key="step.title"
            class="step-row"
            :class="`step-row--${step.tone}`"
          >
            <div class="step-row__index" aria-hidden="true">{{ index + 1 }}</div>
            <div class="step-row__icon" :class="`step-row__icon--${step.tone}`" aria-hidden="true">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="step.iconPath" />
              </svg>
            </div>
            <div class="step-row__body">
              <h4 class="step-row__title">{{ step.title }}</h4>
              <p class="step-row__text">{{ step.text }}</p>
            </div>
          </article>
        </div>
      </section>

      <section class="support-panel tips-section">
        <header class="support-panel__header">
          <div class="support-panel__header-icon support-panel__header-icon--tips" aria-hidden="true">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <h3 class="section-heading">{{ t('suporte.tipsHeading') }}</h3>
            <p class="section-subheading">{{ t('suporte.tipsSubheading') }}</p>
          </div>
        </header>

        <div class="tips-list">
          <article
            v-for="tip in tips"
            :key="tip.title"
            class="tip-row"
            :class="`tip-row--${tip.tone}`"
          >
            <div class="tip-row__icon" :class="`tip-row__icon--${tip.tone}`" aria-hidden="true">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="tip.iconPath" />
              </svg>
            </div>
            <div class="tip-row__body">
              <h4 class="tip-row__title">{{ tip.title }}</h4>
              <p class="tip-row__text">{{ tip.text }}</p>
            </div>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { DISCORD_INVITE_URL } from '@/config/supportContact'

const { t } = useI18n()

const discordInviteUrl = DISCORD_INVITE_URL
const discordIconSrc = '/discord-icon.svg'
const copied = ref(false)

const steps = computed(() => [
  {
    title: t('suporte.steps.join.title'),
    text: t('suporte.steps.join.text'),
    tone: 'discord',
    iconPath: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
  },
  {
    title: t('suporte.steps.ticket.title'),
    text: t('suporte.steps.ticket.text'),
    tone: 'indigo',
    iconPath: 'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z'
  },
  {
    title: t('suporte.steps.wait.title'),
    text: t('suporte.steps.wait.text'),
    tone: 'emerald',
    iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
  }
])

const tips = computed(() => [
  {
    title: t('suporte.tips.organized.title'),
    text: t('suporte.tips.organized.text'),
    tone: 'time',
    iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  {
    title: t('suporte.tips.official.title'),
    text: t('suporte.tips.official.text'),
    tone: 'shield',
    iconPath: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
  },
  {
    title: t('suporte.tips.details.title'),
    text: t('suporte.tips.details.text'),
    tone: 'chat',
    iconPath: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'
  }
])

let copyResetTimer = null

const copyInviteLink = async () => {
  try {
    await navigator.clipboard.writeText(discordInviteUrl)
    copied.value = true
    if (copyResetTimer) clearTimeout(copyResetTimer)
    copyResetTimer = setTimeout(() => {
      copied.value = false
    }, 2500)
  } catch {
    window.prompt(t('suporte.copyPrompt'), discordInviteUrl)
  }
}
</script>

<style scoped>
.suporte-page {
  min-height: 100%;
  background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
}

.modern-header {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-primary);
  padding: 24px 0;
  margin-bottom: 0;
}

.header-container {
  width: 100%;
  padding: 0 24px;
}

.breadcrumb-section {
  margin-bottom: 16px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  flex-wrap: wrap;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
}

.breadcrumb-item:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.breadcrumb-separator {
  color: var(--text-tertiary);
  font-weight: 500;
}

.breadcrumb-current {
  color: var(--text-primary);
  font-weight: 600;
  padding: 6px 12px;
  background: var(--bg-secondary);
  border-radius: 4px;
}

.title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 4px;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.title-icon--support {
  background: linear-gradient(135deg, #5865f2 0%, #4752c4 100%);
  box-shadow: 0 4px 16px rgba(88, 101, 242, 0.45);
}

.main-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
}

.subtitle {
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 4px 0 0;
}

.workspace-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.badge-discord {
  background: rgba(88, 101, 242, 0.12);
  border: 1px solid rgba(88, 101, 242, 0.35);
  color: #5865f2;
}

.badge-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.badge-icon--discord {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: #5865f2;
  color: #fff;
  flex-shrink: 0;
}

.discord-icon {
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

.discord-icon--white {
  filter: brightness(0) invert(1);
}

.discord-icon--sm {
  width: 16px;
  height: 16px;
}

.discord-icon--lg {
  width: 32px;
  height: 32px;
}

.actions-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.action-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  text-decoration: none;
}

.action-button.secondary {
  background: var(--bg-secondary);
  border-color: var(--border-primary);
  color: var(--text-primary);
}

.action-button.secondary:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.action-button.primary {
  background: linear-gradient(135deg, var(--accent-primary), #7c3aed);
  color: #fff;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}

.action-button.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4);
}

.discord-cta {
  background: linear-gradient(135deg, #5865f2 0%, #4752c4 100%) !important;
  box-shadow: 0 4px 14px rgba(88, 101, 242, 0.45) !important;
}

.suporte-content {
  width: 100%;
  max-width: none;
  padding: 28px 24px 48px;
  box-sizing: border-box;
}

.discord-hero {
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  margin-bottom: 36px;
  border: 1px solid rgba(88, 101, 242, 0.35);
  background:
    radial-gradient(ellipse 80% 60% at 10% 0%, rgba(88, 101, 242, 0.35), transparent 55%),
    radial-gradient(ellipse 60% 50% at 90% 100%, rgba(124, 58, 237, 0.2), transparent 50%),
    linear-gradient(145deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 27, 75, 0.88) 100%);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.35);
}

.discord-hero__glow {
  position: absolute;
  inset: -40%;
  background: radial-gradient(circle, rgba(88, 101, 242, 0.25), transparent 55%);
  pointer-events: none;
}

.discord-hero__inner {
  position: relative;
  z-index: 1;
  padding: 36px 32px;
}

.discord-hero__brand {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 20px;
}

.discord-hero__logo {
  width: 56px;
  height: 56px;
  border-radius: 4px;
  background: #5865f2;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(88, 101, 242, 0.5);
}


.discord-hero__eyebrow {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(199, 210, 254, 0.9);
}

.discord-hero__title {
  margin: 0;
  font-size: 1.65rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.25;
}

.discord-hero__lead {
  margin: 0 0 24px;
  max-width: 720px;
  font-size: 1.05rem;
  line-height: 1.65;
  color: rgba(226, 232, 240, 0.92);
}

.discord-hero__lead strong {
  color: #a5b4fc;
  font-weight: 600;
}

.discord-hero__link-box {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 18px;
  margin-bottom: 24px;
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.2);
  max-width: 520px;
}

.discord-hero__link-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(148, 163, 184, 0.95);
}

.discord-hero__link {
  font-size: 1rem;
  font-weight: 600;
  color: #818cf8;
  word-break: break-all;
  text-decoration: none;
  transition: color 0.2s ease;
}

.discord-hero__link:hover {
  color: #c7d2fe;
  text-decoration: underline;
}

.discord-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.hero-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 22px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  border: none;
}

.hero-btn--primary {
  background: #5865f2;
  color: #fff;
  box-shadow: 0 8px 24px rgba(88, 101, 242, 0.45);
}

.hero-btn--primary:hover {
  background: #4752c4;
  transform: translateY(-1px);
}

.hero-btn--ghost {
  background: rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  border: 1px solid rgba(148, 163, 184, 0.35);
}

.hero-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(199, 210, 254, 0.5);
}

.support-panel {
  margin-bottom: 28px;
  padding: 28px 28px 24px;
  border-radius: 4px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.04) inset,
    0 12px 40px rgba(15, 23, 42, 0.08);
}

.support-panel__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-primary);
}

.support-panel__header-icon {
  width: 48px;
  height: 48px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, rgba(88, 101, 242, 0.2), rgba(99, 102, 241, 0.08));
  color: #5865f2;
  border: 1px solid rgba(88, 101, 242, 0.25);
}

.support-panel__header-icon--tips {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.18), rgba(59, 130, 246, 0.08));
  color: #10b981;
  border-color: rgba(16, 185, 129, 0.25);
}

.section-heading {
  margin: 0 0 6px;
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.02em;
}

.section-subheading {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
}

.steps-list,
.tips-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.step-row,
.tip-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 18px;
  width: 100%;
  padding: 18px 22px;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border: 1px solid var(--border-primary);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  box-sizing: border-box;
}

.step-row:hover,
.tip-row:hover {
  transform: translateX(4px);
  border-color: rgba(88, 101, 242, 0.35);
  box-shadow: 0 8px 28px rgba(88, 101, 242, 0.1);
}

.step-row--discord {
  border-left: 4px solid #5865f2;
}

.step-row--indigo {
  border-left: 4px solid var(--accent-primary);
}

.step-row--emerald {
  border-left: 4px solid #10b981;
}

.tip-row--time {
  border-left: 4px solid #3b82f6;
}

.tip-row--shield {
  border-left: 4px solid #10b981;
}

.tip-row--chat {
  border-left: 4px solid #8b5cf6;
}

.step-row__index {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(135deg, #5865f2, #4752c4);
  box-shadow: 0 4px 14px rgba(88, 101, 242, 0.35);
}

.step-row--indigo .step-row__index {
  background: linear-gradient(135deg, var(--accent-primary), #7c3aed);
}

.step-row--emerald .step-row__index {
  background: linear-gradient(135deg, #10b981, #059669);
}

.step-row__icon,
.tip-row__icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-row__icon--discord,
.tip-row__icon--time {
  background: rgba(88, 101, 242, 0.12);
  color: #5865f2;
}

.step-row__icon--indigo {
  background: rgba(99, 102, 241, 0.12);
  color: var(--accent-primary);
}

.step-row__icon--emerald,
.tip-row__icon--shield {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

.tip-row__icon--chat {
  background: rgba(139, 92, 246, 0.12);
  color: #8b5cf6;
}

.tip-row__icon--time {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
}

.step-row__body,
.tip-row__body {
  flex: 1;
  min-width: 0;
}

.step-row__title,
.tip-row__title {
  margin: 0 0 6px;
  font-size: 1.08rem;
  font-weight: 700;
  color: var(--text-primary);
}

.step-row__text,
.tip-row__text {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.6;
  color: var(--text-secondary);
}

@media (max-width: 1024px) {
  .title-section {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 640px) {
  .main-title {
    font-size: 1.5rem;
  }

  .support-panel {
    padding: 20px 16px;
  }

  .step-row,
  .tip-row {
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px;
  }

  .step-row__body,
  .tip-row__body {
    flex: 1 1 100%;
    padding-left: 58px;
  }

  .discord-hero__inner {
    padding: 24px 18px;
  }

  .discord-hero__actions {
    flex-direction: column;
  }

  .hero-btn {
    width: 100%;
  }

  .actions-section {
    width: 100%;
  }

  .action-button {
    flex: 1;
    justify-content: center;
  }
}
</style>
